import { z } from 'zod';
import { hexColourSchema } from './colour.js';

/**
 * A URL a provider may link to: http(s) only, or mailto for support addresses. Blank means
 * none (the API stores it as null; no transform here because these schemas also serialise
 * responses, and Zod can't encode through a transform).
 */
export const brandUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine(
    (v) => v === '' || /^(https?:\/\/[^\s]+|mailto:[^\s@]+@[^\s@]+)$/i.test(v),
    'Enter a full http(s) URL (or a mailto: address).',
  )
  .nullable()
  .default(null);

const shortText = (max: number) => z.string().trim().max(max).nullable().default(null);

/** Blank strings in a branding/login object become null, so "unset" has one spelling. */
export function blankToNull<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = { ...obj };
  for (const [k, v] of Object.entries(out)) {
    if (v === '') out[k] = null;
    else if (v && typeof v === 'object' && !Array.isArray(v))
      out[k] = blankToNull(v as Record<string, unknown>);
  }
  return out as T;
}

/** A reference to one of the tenant's uploaded assets (its UUID), or null. */
export const assetRefSchema = z.uuid().nullable().default(null);

export const LOGO_SLOTS = ['primary', 'light', 'dark', 'compact', 'favicon', 'login'] as const;
export type LogoSlot = (typeof LOGO_SLOTS)[number];
export const LOGO_SLOT_LABELS: Record<LogoSlot, string> = {
  primary: 'Primary logo',
  light: 'Light-mode logo',
  dark: 'Dark-mode logo',
  compact: 'Compact logo / icon',
  favicon: 'Favicon',
  login: 'Login page logo',
};

export const logosSchema = z.object(
  Object.fromEntries(LOGO_SLOTS.map((s) => [s, assetRefSchema])) as {
    [K in LogoSlot]: typeof assetRefSchema;
  },
);
export type Logos = z.infer<typeof logosSchema>;

/** Which logo variant each place in the interface prefers; `auto` picks by theme. */
export const logoUsageSchema = z.object({
  sidebar: z.enum(['auto', 'primary', 'light', 'dark', 'compact']).default('auto'),
  header: z.enum(['auto', 'primary', 'light', 'dark', 'compact']).default('auto'),
  login: z.enum(['auto', 'login', 'primary', 'light', 'dark', 'compact']).default('auto'),
});

export const brandingSchema = z.object({
  /** Shown in the sidebar and browser title. Empty means the panel's name (Settings). */
  displayName: shortText(64),
  companyName: shortText(120),
  panelName: shortText(64),
  tagline: shortText(200),
  footerText: shortText(300),
  supportUrl: brandUrlSchema,
  docsUrl: brandUrlSchema,
  websiteUrl: brandUrlSchema,
  logos: logosSchema.default({
    primary: null,
    light: null,
    dark: null,
    compact: null,
    favicon: null,
    login: null,
  }),
  logoUsage: logoUsageSchema.default({ sidebar: 'auto', header: 'auto', login: 'auto' }),
});
export type Branding = z.infer<typeof brandingSchema>;
export const DEFAULT_BRANDING: Branding = brandingSchema.parse({});

export const loginLinkSchema = z.object({
  label: z.string().trim().min(1).max(60),
  url: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .regex(/^(https?:\/\/[^\s]+|mailto:[^\s@]+@[^\s@]+)$/i, 'Enter a full http(s) URL.'),
});

export const loginPageSchema = z.object({
  heading: shortText(80),
  description: shortText(300),
  showLogo: z.boolean().default(true),
  /** Overrides the theme background behind the login card. Null keeps the theme. */
  backgroundColor: hexColourSchema.nullable().default(null),
  backgroundImage: assetRefSchema,
  /** Darken the background image so the card stays readable. */
  overlay: z.boolean().default(true),
  /** A promotional panel beside the form. */
  promo: z
    .object({
      title: z.string().trim().max(80),
      body: z.string().trim().max(600),
      linkUrl: brandUrlSchema,
      linkLabel: shortText(40),
    })
    .nullable()
    .default(null),
  links: z.array(loginLinkSchema).max(6).default([]),
  footerText: shortText(200),
  formPosition: z.enum(['center', 'left', 'right']).default('center'),
  /** Force a scheme for the login page, or follow the visitor's choice. */
  theme: z.enum(['inherit', 'dark', 'light']).default('inherit'),
});
export type LoginPage = z.infer<typeof loginPageSchema>;
export const DEFAULT_LOGIN_PAGE: LoginPage = loginPageSchema.parse({});
